import {
    useContext, useEffect, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import produce from 'immer';
import { IUploaderFileResponse } from '../../types/components/uploader';
import { IUploaderLayout } from '../../types/layout/uploader';
import useRest from '../useRest';
import { layoutStateSelector } from '../../redux/selectors/layouts';
import { LayoutContext } from '../../contexts/layout';
import { getFileLink } from '../../helpers/uploader';

export const useUploader = ({ dataLink }: IUploaderLayout, manual = false) => {
    const { layoutId } = useContext(LayoutContext);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<IUploaderFileResponse[]>([]);
    const [uploading, setUploading] = useState(false);
    const { objectId } = useParams();
    const layout = useSelector(layoutStateSelector(layoutId));

    const idOwner = objectId || layout.id || '';

    const rest = useRest();

    const getApiUrl = () => `${(new URL(window.location.href)).origin}/api`;

    const deleteFile = async (id: string) => {
        await rest.fetch({
            method: 'post',
            objectName: `/files-service/delete?api=${getApiUrl()}`,
            data: { id },
        });

        getUploadedFiles();
    };

    const getFileData = async (fileId: string): Promise<string> => {
        try {
            const response = await fetch(getFileLink(fileId, idOwner), {
                method: 'get',
            });
            const text = await response.text();
            return text;
        } catch (err) {
            return '';
        }
    };

    const getUploadedFiles = async (): Promise<IUploaderFileResponse[]> => {
        setLoading(true);

        let objectName = dataLink;

        if (typeof idOwner === 'string') {
            objectName = objectName.replace('{idOwner}', idOwner);
        }

        const result = await rest.fetch({
            method: 'post',
            objectName,
            data: {
                id: idOwner,
            },
        });

        if (!result.isError && result.response?.data && Array.isArray(result.response.data.data)) {
            setUploadedFiles(result.response.data.data);
        }

        setLoading(false);

        return result.response?.data?.data || [];
    };

    const uploadFile = async (index: number) => {
        await uploadFiles([filesToUpload[index]]);
        setFilesToUpload(produce(filesToUpload, (draft) => {
            draft.splice(index, 1);
        }));
    };

    const uploadFiles = async (files: File[]) => {
        setUploading(true);

        const formData = new FormData();

        formData.append('idOwner', idOwner || '');

        files.forEach((file) => {
            formData.append('files', file);
        });

        await rest.fetch({
            method: 'post',
            objectName: `/files-service/upload?api=${getApiUrl()}`,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(true);
            }, 500);
        });

        getUploadedFiles();

        setUploading(false);
    };

    const saveTextAsFile = async (fileName: string, text: string) => {
        await rest.fetch({
            method: 'post',
            objectName: `/files-service/saveText?api=${getApiUrl()}`,
            data: { fileName, text, idOwner },
        });
        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(true);
            }, 1500);
        });
    };

    useEffect(() => {
        if (typeof idOwner === 'string' && !manual) {
            getUploadedFiles();
        }
    }, []);

    useEffect(() => {
        // const files = filesToUpload.filter((el) => !el.type.includes('image'));
        // if (filesToUpload.length) {
        //     uploadFiles(files);
        //     setFilesToUpload(produce(filesToUpload, (draft) => draft.filter((el) => el.type.includes('image'))));
        // }
        if (filesToUpload.length) {
            uploadFiles(filesToUpload);
        }
    }, [filesToUpload]);

    return {
        deleteFile,
        filesToUpload,
        getFileData,
        getUploadedFiles,
        loading,
        uploadFile,
        uploadedFiles,
        uploading,
        idOwner,
        saveTextAsFile,
        onFileDrop: setFilesToUpload,
    };
};
